import { Activity, CheckCircle2, Clock3, PauseCircle, Ticket } from 'lucide-react';
import type { ComponentType } from 'react';
import type { StaffEvidenceSummary } from '../../types/staff';
import { useCountUp } from '../../hooks/useCountUp';
import { formatSeconds } from '../../utils/staffProfile';
import { cn } from '../../utils/cn';

interface StaffStatTilesProps {
  evidence: StaffEvidenceSummary;
  windowDays: number;
  /** Unix seconds the payload was generated; relative tiles are frozen at that moment. */
  generatedAt: number;
}

interface Cell {
  label: string;
  value: number | string;
  sub: string;
  icon: ComponentType<{ className?: string }>;
  tone: string;
}

const Number_ = ({ value }: { value: number }) => {
  const shown = useCountUp(value);
  return <>{shown}</>;
};

/**
 * Headline evidence counts as one bento strip. Cells fit the container
 * (auto-fit), so they never collapse into one-word-per-line columns.
 */
export const StaffStatTiles = ({ evidence, windowDays, generatedAt }: StaffStatTilesProps) => {
  const cells: Cell[] = [
    { label: 'Observations', value: evidence.observations, sub: `last ${windowDays} days`, icon: Activity, tone: 'text-accent-300 bg-accent-500/15' },
    {
      label: 'Tickets touched',
      value: evidence.tickets,
      sub: evidence.oldest_at ? `oldest ${formatSeconds(generatedAt - evidence.oldest_at)} ago` : 'no signals yet',
      icon: Ticket,
      tone: 'text-blue-300 bg-blue-500/15',
    },
    { label: 'Healthy tickets', value: evidence.healthy_tickets, sub: 'progressing normally', icon: CheckCircle2, tone: 'text-green-300 bg-green-500/15' },
    { label: 'Stalled tickets', value: evidence.stalled_tickets, sub: 'awaiting follow-up', icon: PauseCircle, tone: 'text-amber-300 bg-amber-500/15' },
    {
      label: 'Since last signal',
      value: evidence.newest_at ? formatSeconds(generatedAt - evidence.newest_at) : '—',
      sub: 'as of this snapshot',
      icon: Clock3,
      tone: 'text-purple-300 bg-purple-500/15',
    },
  ];

  return (
    <div className="glass-card relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-400/50 to-transparent" aria-hidden="true" />
      <dl className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-px bg-glass-200/[0.07]">
        {cells.map((cell) => {
          const Icon = cell.icon;
          return (
            <div key={cell.label} className="p-4 md:p-5 bg-[var(--glass-bg)]">
              <dt className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-glass-400">
                <span className={cn('inline-flex w-6 h-6 items-center justify-center rounded-md', cell.tone)}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                {cell.label}
              </dt>
              <dd className="mt-2 text-3xl font-bold text-white tabular-nums leading-none">
                {typeof cell.value === 'number' ? <Number_ value={cell.value} /> : cell.value}
              </dd>
              <dd className="mt-1.5 text-xs text-glass-500">{cell.sub}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
};
